/**
 * Shared React error-boundary reporter.
 * Sends structured FATAL telemetry via:
 *  1) console (always)
 *  2) gtag (when present)
 *  3) sendBeacon/fetch → /api/internal/front-logs (survives logger-chunk failure)
 *  4) frontend logger (secondary, dynamic import)
 * Never throws — observability must not crash the fallback UI.
 */

function isChunkLoadError(error) {
  const message = String(error?.message || error?.name || '');
  // CRA/webpack: "Loading chunk N failed", "Loading CSS chunk N failed", ChunkLoadError, dynamic import failures
  return /ChunkLoadError|Loading CSS chunk|Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
    message
  );
}

const CHUNK_RELOAD_PREFIX = 'bl_chunk_reload:';

/**
 * One-shot hard reload for deploy/stale-asset chunk failures.
 * Returns true if a reload was triggered (caller should stop rendering recovery UI).
 */
export function maybeReloadForChunkError(error) {
  if (typeof window === 'undefined' || !isChunkLoadError(error)) return false;
  try {
    const key = `${CHUNK_RELOAD_PREFIX}${window.location.pathname}`;
    if (window.sessionStorage.getItem(key)) return false;
    window.sessionStorage.setItem(key, String(Date.now()));
    window.location.reload();
    return true;
  } catch {
    return false;
  }
}

/** Clear one-shot reload markers after a successful paint so future deploys can auto-recover again. */
export function clearChunkReloadMarkers() {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    const toRemove = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i);
      if (key && key.startsWith(CHUNK_RELOAD_PREFIX)) toRemove.push(key);
    }
    toRemove.forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    // ignore
  }
}

function resolveApiBase() {
  try {
    const env = (typeof window !== 'undefined' && window._env) || {};
    const raw =
      env.REACT_APP_API_URL ||
      env.REACT_APP_API_BASE ||
      env.VITE_API_URL ||
      env.VITE_API_BASE ||
      '';
    return String(raw || '').replace(/\/$/, '');
  } catch {
    return '';
  }
}

/**
 * Absolute or same-origin URL for the front-logs ingest endpoint.
 */
export function resolveFrontLogsUrl() {
  const base = resolveApiBase();
  return base ? `${base}/api/internal/front-logs` : '/api/internal/front-logs';
}

/**
 * Build the payload shape expected by POST /api/internal/front-logs.
 */
export function buildBoundaryFrontLogEntry({
  boundary,
  error,
  pathname = null,
  href = null,
  componentName = null,
  componentStack = null,
  extra = {},
} = {}) {
  return {
    level: 'fatal',
    component: boundary || 'ReactBoundary',
    message: `React boundary caught: ${error?.message || 'unknown error'}`,
    data: {
      boundary,
      componentName,
      href,
      pathname,
      isChunkLoadError: isChunkLoadError(error),
      componentStack: componentStack || null,
      errorName: error?.name || null,
      errorMessage: error?.message ? String(error.message).slice(0, 500) : null,
      ...extra,
    },
    timestamp: new Date().toISOString(),
    environment: (typeof process !== 'undefined' && process.env && process.env.NODE_ENV) || 'production',
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'server-side',
  };
}

function postFrontLogs(entry) {
  const url = resolveFrontLogsUrl();
  const body = JSON.stringify({ logs: [entry] });

  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    }
  } catch {
    // fall through to fetch
  }

  try {
    if (typeof fetch === 'function') {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
        credentials: 'omit',
      }).catch(() => {});
    }
  } catch {
    // never throw
  }
}

/**
 * @param {object} options
 * @param {string} options.boundary - Boundary id (e.g. ChunkLoadErrorBoundary)
 * @param {Error} options.error
 * @param {object} [options.errorInfo] - React errorInfo ({ componentStack })
 * @param {string} [options.componentName]
 * @param {object} [options.extra]
 */
export function reportBoundaryError({
  boundary,
  error,
  errorInfo = null,
  componentName = null,
  extra = {},
} = {}) {
  const href = typeof window !== 'undefined' ? window.location.href : null;
  const pathname = typeof window !== 'undefined' ? window.location.pathname : null;
  const payload = {
    boundary,
    componentName,
    href,
    pathname,
    isChunkLoadError: isChunkLoadError(error),
    componentStack: errorInfo?.componentStack || null,
    ...extra,
  };

  // Console always (survives logger sampling / import failure)
  try {
    const original = (typeof window !== 'undefined' && window.__originalConsole) || console;
    original.error?.(`[${boundary}] React boundary caught error`, error, errorInfo, payload);
  } catch {
    // ignore
  }

  // gtag (if present)
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'react_boundary_error', {
        boundary_name: boundary,
        component_name: componentName || 'unknown',
        error_message: String(error?.message || '').slice(0, 200),
        is_chunk_load: isChunkLoadError(error),
        pathname: pathname || 'unknown',
      });
    }
  } catch {
    // ignore
  }

  // Direct front-logs ingest — critical when the logger chunk itself failed to load
  try {
    const entry = buildBoundaryFrontLogEntry({
      boundary,
      error,
      pathname,
      href,
      componentName,
      componentStack: errorInfo?.componentStack || null,
      extra,
    });
    postFrontLogs(entry);
  } catch {
    // ignore
  }

  // Backend telemetry via logger (secondary — may fail during chunk-load storms)
  import('./logger')
    .then(({ createLogger }) => {
      const logger = createLogger(boundary || 'ReactBoundary');
      logger.fatal(
        `React boundary caught: ${error?.message || 'unknown error'}`,
        error instanceof Error ? error : null,
        payload
      );
    })
    .catch(() => {
      // never let telemetry break the fallback UI
    });
}

/**
 * Install once: window.onerror + unhandledrejection → reportBoundaryError.
 * Only reports chunk-load / provider / dynamic-import class failures so
 * third-party scripts (HubSpot, gtag) do not flood front-logs.
 */
export function installGlobalErrorReporting() {
  if (typeof window === 'undefined') return;
  if (window.__buildlogicGlobalErrorReportingInstalled) return;
  window.__buildlogicGlobalErrorReportingInstalled = true;

  const isAppLoadingFailure = (errorOrMessage) => {
    const message = String(
      (errorOrMessage && errorOrMessage.message) || errorOrMessage || ''
    );
    if (isChunkLoadError(errorOrMessage) || isChunkLoadError({ message })) return true;
    return /ToastProvider|useToast must be used|Failed to fetch dynamically imported module|Loading CSS chunk|Loading chunk|Importing a module script failed/i.test(
      message
    );
  };

  window.addEventListener('error', (event) => {
    try {
      const error = event?.error || new Error(event?.message || 'window.error');
      if (!isAppLoadingFailure(error) && !isAppLoadingFailure(event?.message)) return;
      reportBoundaryError({
        boundary: 'window.onerror',
        error,
        extra: {
          surface: 'global',
          filename: event?.filename || null,
          lineno: event?.lineno || null,
          colno: event?.colno || null,
        },
      });
    } catch {
      // ignore
    }
  });

  window.addEventListener('unhandledrejection', (event) => {
    try {
      const reason = event?.reason;
      const error =
        reason instanceof Error
          ? reason
          : new Error(typeof reason === 'string' ? reason : 'unhandledrejection');
      if (!isAppLoadingFailure(error)) return;
      reportBoundaryError({
        boundary: 'unhandledrejection',
        error,
        extra: { surface: 'global' },
      });
    } catch {
      // ignore
    }
  });
}

export { isChunkLoadError };
